/**
 * AppInstance 交付插件（已 registerDeployPlugin；SKU shell + 下单 plugin-editor 已切流量）
 */
import '../../capabilities/registry';
import { registerDeployPlugin } from '../../core/registry';
import { registerSkuResourceModulesEnricher } from '../../platform/preset-hydration';
import { registerResourceConfigs } from '../../platform/resource-watch';
import { registerSkuModuleMeta } from '../../platform/scene-layout/moduleMetaRegistry';
import { appInstanceHeadless } from './headless';
import { enrichAppInstanceSkuResourceModules } from './hydrateFromApiDetail';
import { APP_INSTANCE_RESOURCE_CONFIGS, APP_INSTANCE_SKU_MODULE_META } from './resourceConfigs';

registerSkuModuleMeta('AppInstance', APP_INSTANCE_SKU_MODULE_META);
registerSkuResourceModulesEnricher('AppInstance', enrichAppInstanceSkuResourceModules);
registerResourceConfigs('AppInstance', APP_INSTANCE_RESOURCE_CONFIGS);

registerDeployPlugin({
  headless: appInstanceHeadless,
  loadUi: () =>
    import('./ui').then((m) => ({
      ResourceEditor: m.AppInstanceResourceEditor,
      ResourcePreview: m.AppInstanceResourcePreview,
    })),
});

export type { ApplicationInstanceFormValues, ApplicationInstanceSpec } from './types';
export { DEFAULT_APP_DATA_MOUNT_PATH } from './types';
export type { AppInstanceFieldKey } from './fields';
export { APP_INSTANCE_FIELD_KEYS } from './fields';
export { APP_INSTANCE_CAPABILITIES } from './capabilities';
export { APP_INSTANCE_MODULES, APP_INSTANCE_MODULE_MAP } from './modules';

export {
  APP_INSTANCE_FORM_WATCH_FIELDS,
  formToPayload,
  getAppStorageSubmitError,
  prepareApplicationInstanceSubmit,
  readApplicationInstanceFormValues,
  readApplicationInstanceSpecFromForm,
  resolveAppDataMountPath,
  validateAppStorage,
} from './headless/payload';

export { confirmApplicationImageOnForm } from './headless/imageConfirm';

export { enrichAppInstanceSkuResourceModules, hydrateApplicationInstanceDetailToForm } from './hydrateFromApiDetail';
/** @deprecated 使用 hydrateApplicationInstanceDetailToForm */
export { hydrateApplicationInstanceDetailToForm as detailToForm } from './hydrateFromApiDetail';

export * from './headless/appStorageGroups';

export {
  APP_INSTANCE_RESOURCE_CONFIGS,
  APP_INSTANCE_SKU_MODULE_META,
  computeTemplates,
  dataVolume,
  gpu,
  memory,
  systemDiskSize,
  vcpu,
} from './resourceConfigs';

export {
  APP_INSTANCE_ALL_MODULES,
  APP_INSTANCE_DIRECT_CREATE_STEPS,
  APP_INSTANCE_DIRECT_MERGED_MODULES,
  APP_INSTANCE_ORDER_DELIVERY_RUNTIME,
  APP_INSTANCE_ORDER_MODULE_KEYS,
  APP_INSTANCE_ORDER_MODULE_LIST,
  APP_INSTANCE_ORDER_MODULE_TITLE,
  APP_INSTANCE_SCENES,
  APP_INSTANCE_SKU_MODULE_KEYS,
  APP_INSTANCE_SKU_MODULE_LIST,
  filterAppInstanceDirectMergedModules,
  filterAppInstanceSkuModules,
  resolveAppInstanceSceneConfig,
} from './scenes';
export type {
  AppInstanceDirectCreateStep,
  AppInstanceDirectMergedModuleKey,
  AppInstanceOrderModuleKey,
  AppInstanceSkuModuleKey,
} from './scenes';

export { resetAppInstanceSkuModule, resetAppInstanceSkuModuleFields } from './skuModuleReset';

export { APP_INSTANCE_LINKAGE_RULES, useAppInstanceDeployFormLinkage } from './linkage';
export type { AppInstanceLinkagePropsMap } from './linkage';

export { buildAppInstanceSkuCapabilityOptions } from './skuCapabilityOptions';
export type { BuildAppInstanceSkuCapabilityOptionsInput } from './skuCapabilityOptions';

export {
  resolveAppInstanceOrderInnerRootWatchFields,
  resolveAppInstanceOrderPreviewExtraWatchFields,
  resolveAppInstanceSkuPreviewExtraWatchFields,
} from './orderPreviewWatch';

export {
  appInstanceHeadless,
  appInstancePipeline,
  buildAppInstanceDeliveryMetadata,
  buildAppInstanceOrderDeliveryMetadata,
  filterAppInstanceSpecByEnabledModules,
  isAppInstanceModuleEnabled,
} from './headless';

export {
  AppInstanceAppServiceFields,
  AppInstanceNodeGroupsFields,
  AppInstanceResourceEditor,
  AppInstanceStorageFields,
} from './ui/ResourceEditor';
export type { AppInstanceMaasOptions, AppInstanceResourceEditorProps } from './ui/ResourceEditor';
export { AppInstanceResourcePreview } from './ui/ResourcePreview';
export { ApplicationImageSetting } from './ui/ApplicationImageSetting';
export type { ApplicationImageSettingProps } from './ui/ApplicationImageSetting';
export { AppStorageSetting } from './ui/AppStorageSetting';
export type { AppStorageSettingProps } from './ui/AppStorageSetting';
export { hasPresetAppStorage } from './headless/hasPresetAppStorage';
export { useApplicationImageSetting } from './ui/useApplicationImageSetting';
export { initAppStorageGroupsField, useAppStorageState } from './ui/useAppStorageState';

export { buildAppInstanceFreshDisplayConfig, buildAppInstanceFreshResourceConfigs } from './display';
export { finalizeAppInstanceSubmitDisplayConfig, buildAppInstanceMergedDisplayConfig } from './display/mergeDisplayConfig';
export {
  APP_INSTANCE_CONFIG_PREVIEW_EXCLUDED_MODULES,
  APP_INSTANCE_PRODUCT_CARD_EXCLUDED_DISPLAY_IDS,
  filterAppInstanceProductCardDisplayConfig,
  isAppInstanceProductCardDisplayId,
} from './display/productCard';
export { buildAppInstanceImagePreviewRows } from './display/resolveAppImageSummary';
